import type { ItemWriting } from "./yufa-written-item.js";

/**
 * Reading the sentences a written grammar item carries, each in both
 * writings, off a parsed line. `./yufa-written-line.ts` reads the rest of
 * the line.
 */

/**
 * A record, or nothing for a value that is not one.
 * @param value - What was parsed.
 * @returns The record.
 */
export function recordOf(value: unknown): Record<string, unknown> | undefined {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

/**
 * A sentence in both writings, where both are text.
 * @param value - What the line holds.
 * @returns The sentence.
 */
export function writingOf(value: unknown): ItemWriting | undefined {
  const record = recordOf(value);
  const simplified = record?.["simplified"];
  const traditional = record?.["traditional"];

  return typeof simplified === "string" &&
    typeof traditional === "string" &&
    simplified !== "" &&
    traditional !== ""
    ? { simplified, traditional }
    : undefined;
}

/**
 * Several sentences in both writings, at least one, every one well formed.
 * @param value - What the line holds.
 * @returns The sentences.
 */
export function writingsOf(value: unknown): readonly ItemWriting[] | undefined {
  const read = Array.isArray(value) ? value.map((each) => writingOf(each)) : [];

  return read.length > 0 && read.every((each) => each !== undefined)
    ? read
    : undefined;
}

/**
 * Whether no two of these sentences are written alike.
 * @param writings - The sentences.
 * @returns Whether every one is its own.
 */
export function distinct(writings: readonly ItemWriting[]): boolean {
  return (
    new Set(writings.map((each) => each.simplified)).size === writings.length
  );
}
